import "./MovementCard.scss";

import React, { useMemo, useState } from "react";
import { displayName, formatDate, formatDateTime, getFirstName } from "../../../commons/util/HallAppUtil";
import {
  faArrowDown,
  faArrowUp,
  faBagShopping,
  faRotate,
  faStore,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

import ActionPillButton from "../../../commons/buttons/ActionPillButton";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../commons/HallAppToast";
import ProductInventoryMovementAPI from "../ProductInventoryMovementAPI";

const api = new ProductInventoryMovementAPI();

function getIcon(type) {
  switch (type) {
    case "INPUT":
      return faArrowUp;
    case "OUTPUT":
      return faArrowDown;
    default:
      return faRotate;
  }
}

function getTypeClass(type) {
  switch (type) {
    case "INPUT":
      return "movement-card__type--input";
    case "OUTPUT":
      return "movement-card__type--output";
    default:
      return "movement-card__type--adjustment";
  }
}

function getTypeLabel(type) {
  switch (type) {
    case "INPUT":
      return "Entrada";
    case "OUTPUT":
      return "Saída";
    case "ADJUSTMENT":
      return "Ajuste";
    default:
      return type || "-";
  }
}

function getReasonLabel(reason) {
  const map = {
    PURCHASE: "Compra / reposição",
    INITIAL_STOCK: "Estoque inicial",
    RETURN: "Devolução",
    CONSUMPTION: "Consumo interno",
    LOSS: "Perda / avaria",
    RETURN_SUPPLIER: "Devolução ao fornecedor",
    INVENTORY_COUNT: "Inventário",
    MANUAL_ADJUSTMENT: "Ajuste manual",
    SYSTEM_CORRECTION: "Correção do sistema",
    SALE: "Venda",
    SALE_REVERSAL: "Estorno da venda",
  };

  return map[reason] || reason || "-";
}

function getSaleTypeLabel(saleType) {
  const map = {
    CUSTOMER: "Profissional vendeu para cliente final",
    EMPLOYEE: "Empresa vendeu para profissional",
  };

  return map[saleType] || saleType || "-";
}

function getSaleTypeIcon(saleType) {
  switch (saleType) {
    case "CUSTOMER":
      return faBagShopping;
    case "EMPLOYEE":
      return faStore;
    default:
      return faUser;
  }
}

function shouldShowSaleDetails(movement) {
  return (
    movement?.movementReason === "SALE" ||
    movement?.movementReason === "SALE_REVERSAL" ||
    !!movement?.saleType ||
    !!movement?.employeeId ||
    !!movement?.employeeName
  );
}

function buildSaleDescription(movement) {
  if (!shouldShowSaleDetails(movement)) {
    return null;
  }

  const saleTypeLabel = getSaleTypeLabel(movement?.saleType);
  const employeeName = movement?.employeeName?.trim();
  const employeeId = movement?.employeeId;

  if (movement?.saleType === "CUSTOMER") {
    if (employeeName) {
      return `${saleTypeLabel} • Profissional: ${employeeName}`;
    }

    if (employeeId) {
      return `${saleTypeLabel} • Profissional #${employeeId}`;
    }

    return saleTypeLabel;
  }

  if (movement?.saleType === "EMPLOYEE") {
    if (employeeName) {
      return `${saleTypeLabel} • Recebido por: ${employeeName}`;
    }

    if (employeeId) {
      return `${saleTypeLabel} • Recebido pelo profissional #${employeeId}`;
    }

    return saleTypeLabel;
  }

  if (employeeName) {
    return `Profissional: ${employeeName}`;
  }

  if (employeeId) {
    return `Profissional #${employeeId}`;
  }

  return "Venda";
}

export default function MovementCard({ movement, onReload }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const saleDescription = useMemo(
    () => buildSaleDescription(movement),
    [movement]
  );

  const openDeleteModal = () => setShowDeleteModal(true);

  const closeDeleteModal = () => {
    if (isLoadingDelete) return;
    setShowDeleteModal(false);
  };

  const executeDelete = async () => {
    setIsLoadingDelete(true);
    try {
      await api.delete(movement.id);
      HallAppToast.success("Movimentação excluída com sucesso.");
      setShowDeleteModal(false);
      onReload();
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingDelete(false);
    }
  };

  return (
    <>
      <div className="movement-card">
        <div className="movement-card__header">
          <div
            className={`movement-card__type ${getTypeClass(
              movement.movementType
            )}`}
          >
            <FontAwesomeIcon icon={getIcon(movement.movementType)} />
            <span>{getTypeLabel(movement.movementType)}</span>
          </div>

          <ActionPillButton
            icon={faTrash}
            label="Excluir"
            onClick={openDeleteModal}
            color="#b42318"
            borderColor="#ffd6d6"
          />
        </div>

        <div className="movement-card__body">
          <div className="movement-card__row">
            <span className="label">Motivo</span>
            <span className="value">
              {getReasonLabel(movement.movementReason)}
            </span>
          </div>

          {shouldShowSaleDetails(movement) && (
            <>
              <div className="movement-card__row">
                <span className="label">Detalhe da venda</span>
                <span className="value">
                  <FontAwesomeIcon
                    icon={getSaleTypeIcon(movement.saleType)}
                    style={{ marginRight: 6 }}
                  />
                  {saleDescription}
                </span>
              </div>

              {!!movement.employeeName && (
                <div className="movement-card__row">
                  <span className="label">Profissional</span>
                  <span className="value">{movement.employeeName}</span>
                </div>
              )}
            </>
          )}

          <div className="movement-card__row">
            <span className="label">Quantidade</span>
            <span className="value">{movement.quantity}</span>
          </div>

          <div className="movement-card__row">
            <span className="label">Resp. Criação</span>
            <span className="value">{getFirstName(movement?.responsibleCreateName)}</span>
          </div>

          <div className="movement-card__row">
            <span className="label">Dt. movimento</span>
            <span className="value">{formatDateTime(movement?.movementDate)}</span>
          </div>

          <div className="movement-card__row">
            <span className="label">Estoque</span>
            <span className="value">
              {movement.stockBefore} → {movement.stockAfter}
            </span>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <ConfirmModal
          show={showDeleteModal}
          title="Excluir movimentação"
          isLoading={isLoadingDelete}
          confirm={executeDelete}
          closeModal={closeDeleteModal}
        >
          Deseja realmente excluir essa movimentação de estoque?
        </ConfirmModal>
      )}
    </>
  );
}